import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, Button, Dropdown, Empty, Form, Input, Select, Spin, Table, Tag, Typography } from 'antd';
import type { MenuProps, RefSelectProps, TableColumnsType } from 'antd';
import {
  AppstoreOutlined,
  ColumnWidthOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  MoreOutlined,
  PartitionOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { canManageProductCatalog } from '../modules/products/productCatalogAccessPolicy';
import { DIMENSION_DISPLAY_PROFILES, formatCatalogProductDimensions } from '../utils/productDimensions';
import {
  ProductCategory,
  ProductCategoryAssignedProduct,
  categoryErrorMessage,
  createProductCategoryTag,
  deleteProductCategoryTag,
  fetchProductCategoryAssignmentCandidates,
  fetchProductCategoryAssignments,
  fetchProductCategoryTags,
  replaceProductCategoryAssignments,
  updateProductCategoryTag,
} from '../modules/products/productCategoriesApi';
import { CATEGORY_COLOR_PRESETS } from '../modules/products/category/categoryColor';
import { fetchSupplierOptions } from '../modules/reference-data/api/referenceDataApi';
import type { SupplierOptionDto } from '../dto/reference';
import { formatCategoryPath } from '../modules/products/category/categoryHierarchy';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { ObjectCardHero, ObjectCardSection } from '../components/layout/ObjectCard';
import './ProductCategoryDetail.css';

const { Text } = Typography;
const { TextArea } = Input;

const LIST_PATH = '/app/products?tab=categories';

interface CategoryFormValues {
  name: string;
  description?: string | null;
  color?: string | null;
  dimensionDisplayProfile?: string | null;
  parentId?: string | null;
  defaultSupplierId?: string | null;
}

const toFormValues = (category: ProductCategory | null): CategoryFormValues => ({
  name: category?.name ?? '',
  description: category?.description ?? undefined,
  color: category?.color ?? undefined,
  dimensionDisplayProfile: category?.dimensionDisplayProfile || 'GENERIC',
  parentId: category?.parentId ?? null,
  defaultSupplierId: category?.defaultSupplierId ?? null,
});

// A new subcategory starts with its main category's colour and dimension order.
const toNewSubcategoryValues = (parent: ProductCategory): CategoryFormValues => ({
  ...toFormValues(null),
  color: parent.color ?? undefined,
  dimensionDisplayProfile: parent.dimensionDisplayProfile || 'GENERIC',
  parentId: parent.id,
});

const sameIds = (left: string[], right: string[]) =>
  left.length === right.length && [...left].sort().join('|') === [...right].sort().join('|');

const productSearchLabel = (product: ProductCategoryAssignedProduct) => [
  product.code,
  product.name,
  product.englishName,
  product.ean,
  formatCatalogProductDimensions(product),
].filter(Boolean).join(' ');

/**
 * Category card: edit the category, pick its products and delete it on one
 * page. Replaces the edit and "assign products" modals of the Kategorie tab.
 */
const ProductCategoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestedParentId = isNew ? searchParams.get('parentId') : null;
  const { t } = useTranslation(['products', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm<CategoryFormValues>();
  const canEdit = canManageProductCatalog(authService.getCurrentUser());

  const [suppliers, setSuppliers] = useState<SupplierOptionDto[]>([]);
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [allCategories, setAllCategories] = useState<ProductCategory[]>([]);
  const [candidates, setCandidates] = useState<ProductCategoryAssignedProduct[]>([]);
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [assignedById, setAssignedById] = useState<Map<string, ProductCategoryAssignedProduct>>(new Map());
  const [formDirty, setFormDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const productsRef = useRef<HTMLElement>(null);
  const pickerRef = useRef<RefSelectProps>(null);

  const load = async () => {
    setLoading(true);
    setReady(false);
    try {
      // The backend has no GET /product-categories/:id; the list is small and
      // also feeds the main-category select.
      const [loadedCategories, loadedCandidates, assigned, loadedSuppliers] = await Promise.all([
        fetchProductCategoryTags(),
        fetchProductCategoryAssignmentCandidates(),
        isNew ? Promise.resolve([]) : fetchProductCategoryAssignments(id as string),
        fetchSupplierOptions(),
      ]);
      const loadedCategory = isNew ? null : loadedCategories.find((row) => row.id === id) ?? null;
      if (!isNew && !loadedCategory) {
        setNotFound(true);
        return;
      }
      setNotFound(false);
      setAllCategories(loadedCategories);
      setSuppliers(loadedSuppliers);
      setCategory(loadedCategory);
      setCandidates(loadedCandidates);
      setAssignedById(new Map(assigned.map((product) => [product.id, product])));
      const ids = assigned.map((product) => product.id);
      setSavedIds(ids);
      setSelectedIds(ids);
      const requestedParent = requestedParentId
        ? loadedCategories.find((row) => row.id === requestedParentId && !row.parentId)
        : undefined;
      if (requestedParentId && !requestedParent) {
        feedback.warning(t('products:productCategory.invalidParent'));
      }
      form.setFieldsValue(requestedParent ? toNewSubcategoryValues(requestedParent) : toFormValues(loadedCategory));
      setFormDirty(false);
      setReady(true);
    } catch (error) {
      feedback.error(t('products:productCategory.assignmentLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // "Przypisz produkty" in the list opens the card scrolled to the products section.
  useEffect(() => {
    if (ready && location.hash === '#products') {
      productsRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [ready, location.hash]);

  const productById = useMemo(() => {
    const byId = new Map<string, ProductCategoryAssignedProduct>();
    candidates.forEach((product) => byId.set(product.id, product));
    assignedById.forEach((product, productId) => byId.set(productId, { ...byId.get(productId), ...product }));
    return byId;
  }, [candidates, assignedById]);

  const selectedProducts = useMemo(
    () => selectedIds
      .map((productId) => productById.get(productId))
      .filter((product): product is ProductCategoryAssignedProduct => Boolean(product))
      .sort((a, b) => a.name.localeCompare(b.name, 'pl')),
    [selectedIds, productById]
  );

  const addableProducts = useMemo(
    () => candidates
      .filter((product) => !selectedIds.includes(product.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'pl')),
    [candidates, selectedIds]
  );

  const parentId = Form.useWatch('parentId', form) ?? null;
  const defaultSupplierId = Form.useWatch('defaultSupplierId', form) ?? null;
  const dimensionProfile = Form.useWatch('dimensionDisplayProfile', form) || 'GENERIC';
  const defaultSupplierName = suppliers.find((supplier) => supplier.id === defaultSupplierId)?.name ?? null;
  const parentCategory = allCategories.find((row) => row.id === parentId) ?? null;
  const hasSubcategories = (category?.childCount ?? 0) > 0;
  // Only main categories can be parents, never the edited category itself.
  const parentOptions = useMemo(
    () => allCategories
      .filter((row) => !row.parentId && row.id !== category?.id)
      .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
      .map((row) => ({ value: row.id, label: row.name })),
    [allCategories, category?.id]
  );

  const assignmentsDirty = !sameIds(savedIds, selectedIds);
  const dirty = formDirty || assignmentsDirty;
  const disabled = !canEdit || !ready || saving;

  const handleBack = () => {
    if (dirty && !isNew) {
      feedback.warning(t('products:productCategory.card.leaveBlocked'));
      return;
    }
    goBack(LIST_PATH);
  };

  const handleDiscard = () => {
    if (isNew) {
      navigate(LIST_PATH);
      return;
    }
    form.setFieldsValue(toFormValues(category));
    setSelectedIds(savedIds);
    setFormDirty(false);
  };

  const handleSave = async () => {
    let values: CategoryFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      color: values.color || null,
      dimensionDisplayProfile: values.dimensionDisplayProfile || 'GENERIC',
      parentId: values.parentId || null,
      defaultSupplierId: values.defaultSupplierId || null,
    };
    setSaving(true);
    try {
      if (isNew) {
        const created = await createProductCategoryTag(payload);
        if (selectedIds.length > 0) {
          await replaceProductCategoryAssignments(created.id, selectedIds);
        }
        feedback.success(t('products:productCategory.createdSuccess', { name: payload.name }));
        navigate(`/app/products/categories/${created.id}`, { replace: true });
        return;
      }
      if (formDirty) {
        await updateProductCategoryTag(category!.id, payload);
      }
      if (assignmentsDirty) {
        await replaceProductCategoryAssignments(category!.id, selectedIds);
      }
      feedback.success(t('products:productCategory.updatedSuccess', { name: payload.name }));
      await load();
    } catch (error: any) {
      feedback.error(categoryErrorMessage(error, t, t('products:productCategory.saveFailed', { name: payload.name })));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!category) return;
    if (hasSubcategories) {
      feedback.warning(t('products:productCategory.hasSubcategories'));
      return;
    }
    const productCount = savedIds.length;
    modal.confirm({
      title: t('products:productCategory.deleteConfirmTitle'),
      content: productCount > 0
        ? (category.parentId
          ? t('products:productCategory.deleteSubcategoryWithProducts', {
            count: productCount,
            parent: category.parentName,
          })
          : t('products:productCategory.deleteConfirmWithProducts', { count: productCount }))
        : t('products:productCategory.deleteConfirm'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteProductCategoryTag(category.id);
          feedback.success(t('products:productCategory.deletedSuccess', { name: category.name }));
          navigate(LIST_PATH);
        } catch (error: any) {
          feedback.error(categoryErrorMessage(error, t, t('products:productCategory.deleteFailed', { name: category.name })));
        }
      },
    });
  };

  const headerMenu: MenuProps['items'] = [
    {
      key: 'delete',
      danger: true,
      icon: <DeleteOutlined />,
      label: t('products:productCategory.card.delete'),
      disabled: !canEdit || saving,
    },
  ];

  const rowMenu: MenuProps['items'] = [
    { key: 'open', icon: <EyeOutlined />, label: t('products:productCategory.card.openProduct') },
    ...(canEdit ? [{
      key: 'remove',
      danger: true,
      icon: <MinusCircleOutlined />,
      label: t('products:productCategory.card.removeProduct'),
      disabled,
    }] : []),
  ];

  const handleRowAction = (key: string, product: ProductCategoryAssignedProduct) => {
    if (key === 'open') {
      navigate(`/app/products/${product.id}`);
      return;
    }
    if (key === 'remove') {
      setSelectedIds((ids) => ids.filter((productId) => productId !== product.id));
    }
  };

  const columns: TableColumnsType<ProductCategoryAssignedProduct> = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_value, product) => (
        <Dropdown
          menu={{ items: rowMenu, onClick: ({ key }) => handleRowAction(key, product) }}
          trigger={['click']}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            title={t('common:actions')}
            aria-label={t('common:actions')}
          />
        </Dropdown>
      ),
    },
    {
      title: t('products:productCategory.card.product'),
      key: 'name',
      render: (_value, product) => {
        const isPending = !savedIds.includes(product.id);
        const previousCategory = isPending && product.productCategoryId && product.productCategoryId !== category?.id
          ? formatCategoryPath(product.productCategoryName, product.productCategoryParentName)
          : null;
        return (
          <div className="category-card-product">
            <button type="button" className="category-card-product__name" title={product.name} onClick={() => navigate(`/app/products/${product.id}`)}>
              <span>{product.name}</span>
            </button>
            {(product.englishName || product.ean) && (
              <small className="category-card-product__meta">
                {[product.englishName, product.ean].filter(Boolean).join(' · ')}
              </small>
            )}
            {isPending && (
              <Tag color="blue" className="category-card-product__pending">
                {previousCategory
                  ? t('products:productCategory.card.pendingMove', { name: previousCategory })
                  : t('products:productCategory.card.pendingAdd')}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t('products:productCategory.card.dimensions'),
      key: 'dimensions',
      width: 280,
      render: (_value, product) => formatCatalogProductDimensions(product) || <Text type="secondary">—</Text>,
    },
  ];

  if (notFound) {
    return (
      <div className="obj-card category-card">
        <section className="obj-section">
          <Empty description={t('products:productCategory.card.notFound')}>
            <Button onClick={() => navigate(LIST_PATH)}>{t('products:productCategory.card.back')}</Button>
          </Empty>
        </section>
      </div>
    );
  }

  const title = isNew
    ? (parentCategory ? t('products:productCategory.createSubcategory') : t('products:productCategory.create'))
    : (category?.name ?? '');

  return (
    <div className={`obj-card category-card${(dirty || isNew) && canEdit ? ' obj-card--editable' : ''}`} data-testid="category-card">
      <Spin spinning={loading}>
        <div className="obj-card__stack">
          <ObjectCardHero
            testId="category-card-header"
            kind={parentCategory ? t('products:productCategory.card.kindSub') : t('products:productCategory.card.kind')}
            name={title || '—'}
            backLabel={t('products:productCategory.card.back')}
            onBack={handleBack}
            meta={[
              parentCategory && (
                <>
                  {t('products:productCategory.card.chips.parent')}:{' '}
                  <Button type="link" className="obj-link" onClick={() => navigate(`/app/products/categories/${parentCategory.id}`)}>
                    {parentCategory.name}
                  </Button>
                </>
              )
            ]}
            actions={!isNew && canEdit && (
              <Dropdown
                menu={{ items: headerMenu, onClick: ({ key }) => key === 'delete' && handleDelete() }}
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} data-testid="category-more-actions">
                  {t('products:productCategory.card.more')}
                </Button>
              </Dropdown>
            )}
            facts={[
              { key: 'products', label: t('products:productCategory.card.chips.products'), icon: <AppstoreOutlined />, value: selectedIds.length, testId: 'category-chip-products' },
              ...(!isNew && !category?.parentId ? [{
                key: 'subcategories',
                label: t('products:productCategory.card.chips.subcategories'),
                icon: <PartitionOutlined />,
                value: category?.childCount ?? 0,
                testId: 'category-chip-subcategories'
              }] : []),
              { key: 'supplier', label: t('products:productCategory.card.chips.defaultSupplier'), icon: <ShopOutlined />, value: defaultSupplierName || '—', testId: 'category-chip-supplier' },
              {
                key: 'dimensions',
                label: t('products:productCategory.card.chips.dimensions'),
                icon: <ColumnWidthOutlined />,
                // Short form fits a quarter-width tile at 1440 px; the full name is in the form below.
                value: t(`products:productCategory.dimensionProfilesShort.${dimensionProfile}`),
                testId: 'category-chip-dimensions'
              }
            ]}
          />

          <ObjectCardSection
            testId="category-section-details" tone="blue"
            title={t('products:productCategory.card.detailsTitle')}
            icon={<EditOutlined />}
          >
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              disabled={disabled}
              initialValues={toFormValues(null)}
              onValuesChange={() => setFormDirty(true)}
            >
              {/* Field widths follow content (DESIGN.md, Forms): short fields in a
                  narrow column, the description as a tall text area beside them. */}
              <div className="obj-form category-card-details">
                <Form.Item
                  name="name"
                  label={t('products:productCategory.name')}
                  rules={[{ required: true, whitespace: true, message: t('products:productCategory.nameRequired') }]}
                >
                  <Input placeholder={t('products:productCategory.namePlaceholder')} maxLength={120} />
                </Form.Item>
                <Form.Item
                  name="parentId"
                  label={t('products:productCategory.parent')}
                  tooltip={hasSubcategories ? undefined : t('products:productCategory.parentHint')}
                  extra={hasSubcategories ? t('products:productCategory.parentLockedHint') : undefined}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    disabled={disabled || hasSubcategories}
                    placeholder={t('products:productCategory.parentNone')}
                    options={parentOptions}
                  />
                </Form.Item>
                <Form.Item name="defaultSupplierId" label={t('products:productCategory.defaultSupplier')}
                  tooltip={t('products:productCategory.defaultSupplierHint')}>
                  <Select allowClear showSearch optionFilterProp="label" placeholder={t('products:productCategory.chooseSupplier')}
                    options={suppliers.map(supplier => ({ value: supplier.id, label: supplier.name }))} />
                </Form.Item>
                <Form.Item name="color" label={t('products:productCategory.color')}>
                  <Select
                    allowClear
                    placeholder={t('products:productCategory.colorNone')}
                  >
                    {CATEGORY_COLOR_PRESETS.map((color) => (
                      <Select.Option key={color} value={color}>
                        <span className="category-card-swatch" style={{ background: color }} />
                        {t(`products:productCategory.colorNames.${color.slice(1)}`)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="dimensionDisplayProfile"
                  label={t('products:productCategory.dimensionDisplayProfile')}
                  tooltip={t('products:productCategory.dimensionDisplayProfileHint')}
                >
                  <Select>
                    {DIMENSION_DISPLAY_PROFILES.map((profile) => (
                      <Select.Option key={profile} value={profile}>
                        {t(`products:productCategory.dimensionProfiles.${profile}`)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="description" className="category-card-details__description" label={t('products:productCategory.description')}>
                  <TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={500} placeholder={t('products:productCategory.descriptionPlaceholder')} />
                </Form.Item>
              </div>
            </Form>
          </ObjectCardSection>

          <ObjectCardSection
            className="category-card-products"
            id="products"
            sectionRef={productsRef}
            testId="category-section-products" tone="indigo"
            title={t('products:productCategory.card.productsTitle')}
            icon={<AppstoreOutlined />}
            count={selectedIds.length}
            hint={t('products:productCategory.card.productsHint')}
            actions={canEdit && (
                <Select
                  ref={pickerRef}
                  className="category-card-products__picker"
                  classNames={{ popup: { root: 'category-card-products__popup' } }}
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  showSearch
                  value={null}
                  disabled={disabled}
                  placeholder={t('products:productCategory.card.addProduct')}
                  optionFilterProp="label"
                  notFoundContent={t('products:productCategory.card.noCandidates')}
                  searchValue={productSearch}
                  onSearch={setProductSearch}
                  onChange={(productId: string) => {
                    setSelectedIds((ids) => [...ids, productId]);
                    setProductSearch('');
                    pickerRef.current?.blur();
                  }}
                  options={addableProducts.map((product) => ({
                    value: product.id,
                    label: productSearchLabel(product),
                    product,
                  }))}
                  optionRender={({ data }) => {
                    const product = data.product as ProductCategoryAssignedProduct;
                    return (
                      <div className="category-card-option">
                        <span>{product.name}</span>
                        <small>
                          {[
                            formatCatalogProductDimensions(product),
                            product.productCategoryName
                              ? t('products:productCategory.currentlyInCategory', {
                                name: formatCategoryPath(product.productCategoryName, product.productCategoryParentName),
                              })
                              : t('products:productCategory.none'),
                          ].filter(Boolean).join(' · ')}
                        </small>
                      </div>
                    );
                  }}
                />
            )}
          >
            <Table<ProductCategoryAssignedProduct>
              columns={columns}
              dataSource={selectedProducts}
              rowKey="id"
              size="small"
              tableLayout="fixed"
              scroll={{ x: 760 }}
              pagination={selectedProducts.length > 25 ? { defaultPageSize: 25, showSizeChanger: true } : false}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('products:productCategory.noAssignedProducts')} /> }}
            />
          </ObjectCardSection>
        </div>
      </Spin>

      {(dirty || isNew) && canEdit && (
        <div className="obj-savebar" role="region" aria-label={t('common:save')}>
          <span className="obj-savebar__status">
            {isNew ? t('products:productCategory.card.savebarNew') : t('products:productCategory.card.savebarDirty')}
          </span>
          <Button onClick={handleDiscard} disabled={saving}>{t('products:productCategory.card.discard')}</Button>
          <Button type="primary" onClick={handleSave} loading={saving} disabled={!ready}>
            {t('common:save')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductCategoryDetail;
